/*
 * Healthcare professional entry gate.
 * The page is rendered with html.hcp-gate-locked (see layout/theme.liquid), which hides the
 * main content before first paint. This element makes the dialog accessible and unlocks the
 * page once the visitor confirms. Confirmation is a single sessionStorage flag.
 */
class HcpGate extends HTMLElement {
  constructor() {
    super();
    this.storageKey = this.dataset.storageKey || "hxHcpConfirmed";
    this.dialog = this.querySelector('[role="dialog"]');
    this.questionPanel = this.querySelector('[data-hcp-panel="question"]');
    this.deniedPanel = this.querySelector('[data-hcp-panel="denied"]');
    this.inertElements = [];
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  connectedCallback() {
    this.querySelector("[data-hcp-confirm]").addEventListener("click", this.confirm.bind(this));
    this.querySelector("[data-hcp-decline]").addEventListener("click", this.decline.bind(this));

    if (this.hasAttribute("data-design-mode")) {
      this.bindEditorEvents();
      return;
    }

    window.addEventListener("pageshow", (event) => {
      if (event.persisted && this.isConfirmed()) this.unlock();
    });

    if (this.isConfirmed()) {
      this.unlock();
    } else {
      this.open();
    }
  }

  isConfirmed() {
    try {
      return window.sessionStorage.getItem(this.storageKey) === "true";
    } catch (error) {
      return false;
    }
  }

  open() {
    document.documentElement.classList.add("hcp-gate-locked");
    this.showPanel("question");
    this.setBackgroundInert(true);
    document.addEventListener("keydown", this.onKeyDown, true);
    this.dialog.focus();
  }

  confirm() {
    if (this.hasAttribute("data-design-mode")) return this.closePreview();
    try {
      window.sessionStorage.setItem(this.storageKey, "true");
    } catch (error) {
      // Storage unavailable (e.g. blocked); unlock for this page view only.
    }
    this.unlock();
    const main = document.getElementById("MainContent");
    if (main) main.focus({ preventScroll: true });
  }

  decline() {
    this.showPanel('denied');
    this.deniedPanel.querySelector('[tabindex="-1"]').focus();
  }

  unlock() {
    document.removeEventListener("keydown", this.onKeyDown, true);
    this.setBackgroundInert(false);
    document.documentElement.classList.remove("hcp-gate-locked");
    this.classList.remove("is-open");
  }

  showPanel(name) {
    this.questionPanel.hidden = name !== 'question';
    this.deniedPanel.hidden = name !== 'denied';

    const panel = name === 'denied' ? this.deniedPanel : this.questionPanel;
    const label = panel.querySelector('[data-hcp-label]');
    const description = panel.querySelector('[data-hcp-description]');
    this.dialog.setAttribute('aria-labelledby', label.id);
    if (description) {
      this.dialog.setAttribute('aria-describedby', description.id);
    } else {
      this.dialog.removeAttribute('aria-describedby');
    }
  }

  setBackgroundInert(state) {
    if (!state) {
      this.inertElements.forEach((element) => {
        element.inert = false;
        element.removeAttribute("aria-hidden");
      });
      this.inertElements = [];
      return;
    }

    const container = this.closest(".shopify-section") || this;
    Array.from(document.body.children).forEach((element) => {
      if (element === container || element.contains(container) || element.inert) return;
      if (["SCRIPT", "STYLE", "LINK", "NOSCRIPT", "TEMPLATE"].includes(element.tagName)) return;
      element.inert = true;
      element.setAttribute("aria-hidden", "true");
      this.inertElements.push(element);
    });
  }

  getFocusableElements() {
    return Array.from(this.dialog.querySelectorAll('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')).filter((element) => element.offsetParent !== null);
  }

  onKeyDown(event) {
    if (event.key === "Escape") {
      // The gate cannot be dismissed without answering.
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (event.key !== "Tab") return;

    const focusable = this.getFocusableElements();
    if (!focusable.length) {
      event.preventDefault();
      this.dialog.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    const outside = !this.dialog.contains(active);

    if (event.shiftKey && (active === first || active === this.dialog || outside)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || outside)) {
      event.preventDefault();
      first.focus();
    }
  }

  // Theme editor: never lock the page, preview the dialog only while the section is selected.
  bindEditorEvents() {
    const sectionId = (this.closest(".shopify-section") || {}).id;
    document.addEventListener("shopify:section:select", (event) => {
      if (`shopify-section-${event.detail.sectionId}` !== sectionId) return;
      this.showPanel("question");
      this.classList.add("is-open");
    });
    document.addEventListener("shopify:section:deselect", (event) => {
      if (`shopify-section-${event.detail.sectionId}` !== sectionId) return;
      this.closePreview();
    });
  }

  closePreview() {
    this.classList.remove("is-open");
  }
}

if (!customElements.get("hcp-gate")) {
  customElements.define("hcp-gate", HcpGate);
}
